import Decimal from "decimal.js";
import { roundCurrency, toDecimal } from "@/lib/utils/math";
import { getCPI, CPI_MAX_YEAR } from "@/lib/data/cpi-by-year";

export interface UnifiedChartInput {
  historicalAmount: number;
  historicalYear: number;
  inflationRate: number;
  projectionYears: number;
}

export interface UnifiedChartPoint {
  year: number;
  amount: number;
  /** Year-over-year inflation % (null for first year) */
  inflationYoy?: number | null;
}

/**
 * Build chart data: historical (CPI) from historicalYear to today, then projection (rate) into future.
 */
export function buildUnifiedChartData(input: UnifiedChartInput): UnifiedChartPoint[] {
  const { historicalAmount, historicalYear, inflationRate, projectionYears } = input;

  const cpiFrom = getCPI(historicalYear);
  if (cpiFrom == null) return [];

  const points: UnifiedChartPoint[] = [];
  const r = new Decimal(inflationRate).div(100);

  // Historical: each year from historicalYear to CPI_MAX_YEAR
  for (let y = historicalYear; y <= CPI_MAX_YEAR; y++) {
    const cpiY = getCPI(y);
    if (cpiY == null) continue;
    const amount = roundCurrency(toDecimal(historicalAmount * (cpiY / cpiFrom)));
    const prevAmount = points.length > 0 ? points[points.length - 1].amount : null;
    const inflationYoy =
      prevAmount != null && prevAmount > 0
        ? roundCurrency(toDecimal((amount - prevAmount) / prevAmount * 100))
        : null;
    points.push({ year: y, amount, inflationYoy });
  }

  const todayAmount = points.length > 0 ? points[points.length - 1].amount : historicalAmount;

  // Projection: from CPI_MAX_YEAR to CPI_MAX_YEAR + projectionYears
  for (let i = 1; i <= projectionYears; i++) {
    const yearT = new Decimal(i);
    const yearGrowth = new Decimal(1).add(r).pow(yearT);
    const amount = roundCurrency(toDecimal(todayAmount).mul(yearGrowth));
    const prevAmount = points[points.length - 1].amount;
    const inflationYoy =
      prevAmount > 0 ? roundCurrency(toDecimal((amount - prevAmount) / prevAmount * 100)) : inflationRate;
    points.push({ year: CPI_MAX_YEAR + i, amount, inflationYoy });
  }

  return points;
}

export interface InflationInput {
  /** Amount in today's dollars */
  amount: number;
  /** Annual inflation rate (percent) */
  inflationRate: number;
  /** Number of years */
  years: number;
}

export interface InflationResult {
  /** Amount needed in the future to have same purchasing power */
  futureEquivalent: number;
  /** Amount from the past that had same purchasing power */
  pastEquivalent: number;
  /** Total erosion of purchasing power (futureEquivalent - amount) */
  purchasingPowerLost: number;
  /** Inflation multiplier: futureEquivalent / amount = (1+r)^t */
  inflationMultiplier: number;
  chartData: { year: number; futureEquivalent: number; todayValue: number }[];
}

/**
 * Solve for rate: target = amount × (1 + r)^t => r = (target/amount)^(1/t) - 1
 */
export function solveInflationRate(
  amount: number,
  targetFutureAmount: number,
  years: number
): number | null {
  if (amount <= 0 || targetFutureAmount <= 0 || years <= 0) return null;
  const ratio = targetFutureAmount / amount;
  if (ratio < 1) return null; // Target less than amount = deflation, we don't support
  const r = Math.pow(ratio, 1 / years) - 1;
  return Math.round(r * 1000) / 10; // Percent, 1 decimal
}

/**
 * Future equivalent: amount × (1 + r)^t
 * Past equivalent: amount / (1 + r)^t = amount × (1 + r)^(-t)
 */
export function calculateInflation(input: InflationInput): InflationResult {
  const { amount, inflationRate, years } = input;

  const a = new Decimal(amount);
  const r = new Decimal(inflationRate).div(100);
  const t = new Decimal(years);

  const growthFactor = new Decimal(1).add(r).pow(t);
  const futureDecimal = a.mul(growthFactor);
  const pastDecimal = a.div(growthFactor);

  const futureEquivalent = roundCurrency(futureDecimal);
  const pastEquivalent = roundCurrency(pastDecimal);
  const purchasingPowerLost = roundCurrency(futureDecimal.minus(a));
  const inflationMultiplier = Number(growthFactor.toDecimalPlaces(2).toNumber());

  const chartData: {
    year: number;
    futureEquivalent: number;
    todayValue: number;
  }[] = [];

  for (let y = 0; y <= years; y++) {
    const yearT = new Decimal(y);
    const yearGrowth = new Decimal(1).add(r).pow(yearT);
    const yearFuture = a.mul(yearGrowth);
    const yearToday = a.div(yearGrowth); // purchasing power of amount at year y
    chartData.push({
      year: y,
      futureEquivalent: roundCurrency(yearFuture),
      todayValue: roundCurrency(yearToday),
    });
  }

  return {
    futureEquivalent,
    pastEquivalent,
    purchasingPowerLost,
    inflationMultiplier,
    chartData,
  };
}

export interface HistoricalInflationInput {
  /** Amount in the past year's dollars */
  amount: number;
  /** Year the amount is from (e.g., 1975) */
  fromYear: number;
  /** Year to convert to (default: current year) */
  toYear?: number;
}

export interface HistoricalInflationResult {
  /** Amount in toYear dollars (same purchasing power) */
  equivalentAmount: number;
  /** Total inflation over the period (as decimal, e.g., 0.5 = 50%) */
  totalInflation: number;
}

/**
 * Convert amount from one year to another using actual CPI data.
 * equivalentAmount = amount × (CPI_toYear / CPI_fromYear)
 */
export function calculateHistoricalInflation(
  input: HistoricalInflationInput
): HistoricalInflationResult | null {
  const { amount, fromYear, toYear = CPI_MAX_YEAR } = input;

  const cpiFrom = getCPI(fromYear);
  const cpiTo = getCPI(toYear);

  if (cpiFrom == null || cpiTo == null) return null;

  const ratio = cpiTo / cpiFrom;
  const equivalentAmount = roundCurrency(toDecimal(amount * ratio));
  const totalInflation = ratio - 1;

  return {
    equivalentAmount,
    totalInflation,
  };
}
